import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../config';
import { 
  XMarkIcon, 
  MagnifyingGlassIcon, 
  MapPinIcon, 
  UserIcon,
  CalendarIcon,
  ClockIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

const AppointmentBookingModal = ({ isOpen, onClose, patientId }) => {
  const [step, setStep] = useState('search'); // 'search', 'slots', 'confirm'
  const [searchParams, setSearchParams] = useState({
    name: '',
    specialty: '',
    location: ''
  });
  const [practitioners, setPractitioners] = useState([]);
  const [loadingPractitioners, setLoadingPractitioners] = useState(false);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [appointmentSlots, setAppointmentSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [serviceType, setServiceType] = useState('consultation');
  const [bookingAppointment, setBookingAppointment] = useState(false);
  const [appointmentNote, setAppointmentNote] = useState('');
  const [specialties, setSpecialties] = useState([]);
  const [loadingSpecialties, setLoadingSpecialties] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Load specialties from API on component mount
  useEffect(() => {
    const loadSpecialties = async () => {
      setLoadingSpecialties(true);
      try {
        const response = await axios.get(`${API_URL}/api/specialties`);
        if (response.data && response.data.specialties) {
          setSpecialties(response.data.specialties.sort());
        }
      } catch (error) {
        console.error('Error loading specialties:', error);
        // Fallback to a basic list if API fails
        setSpecialties([
          'Cardiology',
          'Dermatology',
          'Endocrinology',
          'Family Medicine',
          'Gastroenterology',
          'General Surgery',
          'Internal Medicine',
          'Neurology',
          'Obstetrics and Gynecology',
          'Oncology',
          'Ophthalmology',
          'Orthopaedic Surgery',
          'Pediatrics',
          'Psychiatry',
          'Radiology',
          'Urology'
        ]);
      } finally {
        setLoadingSpecialties(false);
      }
    };

    if (isOpen) {
      loadSpecialties();
    }
  }, [isOpen]);

  // Specialties are now loaded from the API via useEffect

  // Service types for appointment booking
  const serviceTypes = [
    { value: 'consultation', label: 'Consultation' },
    { value: 'follow-up', label: 'Follow-up' },
    { value: 'procedure', label: 'Procedure' },
    { value: 'emergency', label: 'Emergency' },
    { value: 'routine', label: 'Routine Check-up' }
  ];

  const handleSearch = async () => {
    setErrorMessage('');
    if (!searchParams.name && !searchParams.specialty) {
      setErrorMessage('Please enter at least one search criteria');
      return;
    }

    setLoadingPractitioners(true);
    try {
      const params = new URLSearchParams();
      if (searchParams.name) params.append('name', searchParams.name);
      if (searchParams.specialty) params.append('specialty', searchParams.specialty);

      // Determine search method for user feedback
      const searchMethod = searchParams.specialty && !searchParams.name ? 
        `Searching by specialty (${searchParams.specialty})` : 
        searchParams.name ? 
        `Searching by name (${searchParams.name})` : 
        'Searching practitioners';

      console.log(searchMethod);
      const response = await axios.get(`${API_URL}/api/practitioners?${params.toString()}`);
      
      if (response.data && response.data.entry) {
        const practitionersData = response.data.entry.map(entry => entry.resource);
        console.log('Frontend received practitioners:', practitionersData);
        console.log('Location data for first practitioner:', practitionersData[0]?.location);
        setPractitioners(practitionersData);
      } else {
        setPractitioners([]);
      }
    } catch (error) {
      console.error('Error searching practitioners:', error);
      setErrorMessage('Error searching for practitioners. Please try again.');
    } finally {
      setLoadingPractitioners(false);
    }
  };

  const handlePractitionerSelect = async (practitioner) => {
    setErrorMessage('');
    setSelectedPractitioner(practitioner);
    setStep('slots');
    
    // Get appointment slots for the next 30 days
    const startDate = new Date().toISOString().split('T')[0];
    const endDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    setLoadingSlots(true);
    try {
      const params = new URLSearchParams();
      params.append('practitioner', practitioner.id);
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      if (serviceType) params.append('serviceType', serviceType);

      const response = await axios.get(`${API_URL}/api/appointment-slots?${params.toString()}`);
      
      if (response.data && response.data.entry) {
        setAppointmentSlots(response.data.entry.map(entry => entry.resource));
      } else {
        setAppointmentSlots([]);
      }
    } catch (error) {
      console.error('Error fetching appointment slots:', error);
      setErrorMessage('Error fetching appointment slots. Please try again.');
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleSlotSelect = (slot) => {
    setErrorMessage('');
    setSelectedSlot(slot);
    setSelectedDate(new Date(slot.start));
    setStep('confirm');
  };

  const handleConfirmBooking = async () => {
    setErrorMessage('');
    if (!selectedSlot || !selectedPractitioner || !patientId) {
      setErrorMessage('Please select a slot and practitioner');
      return;
    }

    setBookingAppointment(true);

    try {
      // Prepare the booking data using the appointment ID from the selected slot
      const bookingData = {
        patientId: patientId,
        appointmentId: selectedSlot.id,
        appointmentNote: appointmentNote
      };

      console.log('Booking appointment with data:', bookingData);

      // Call the booking API
      const response = await axios.post(`${API_URL}/api/book-appointment`, bookingData);
      
      if (response.data.success) {
        alert(`Appointment booked successfully!\n\nAppointment ID: ${response.data.appointment.id}\nDate: ${new Date(selectedSlot.start).toLocaleDateString()}\nTime: ${new Date(selectedSlot.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`);
        
        // Reset the modal state
        onClose();
        setStep('search');
        setSelectedPractitioner(null);
        setSelectedSlot(null);
        setSelectedDate(null);
        setAppointmentNote('');
        setSearchParams({ name: '', specialty: '', location: '' });
        setPractitioners([]);
        setAppointmentSlots([]);
      } else {
        throw new Error('Booking failed');
      }
    } catch (error) {
      console.error('Error booking appointment:', error);
      const errorMessageText = error.response?.data?.error || error.message || 'Error booking appointment. Please try again.';
      setErrorMessage(`Error booking appointment: ${errorMessageText}`);
    } finally {
      setBookingAppointment(false);
    }
  };

  const formatPractitionerName = (practitioner) => {
    if (practitioner.name?.[0]?.text) {
      return practitioner.name[0].text;
    }
    
    const name = practitioner.name?.[0];
    if (name) {
      const prefix = name.prefix?.join(' ') || '';
      const given = name.given?.join(' ') || '';
      const family = name.family || '';
      return `${prefix} ${given} ${family}`.trim();
    }
    
    return 'Unknown Practitioner';
  };

  const formatSpecialty = (practitioner) => {
    // If specialty was added from PractitionerRole search, use that
    if (practitioner.specialty) {
      return practitioner.specialty;
    }
    
    // Otherwise, try to get from qualifications
    const specialty = practitioner.qualification?.[0]?.code?.coding?.[0]?.display ||
                     practitioner.qualification?.[0]?.code?.text ||
                     'General Practice';
    return specialty;
  };

  const formatLocation = (practitioner) => {
    console.log('formatLocation called with practitioner:', practitioner);
    console.log('practitioner.location:', practitioner.location);
    
    // If location was added from PractitionerRole search, use that
    if (practitioner.location && practitioner.location !== 'Location not specified') {
      console.log('Using resolved location:', practitioner.location);
      return practitioner.location;
    }
    
    // Otherwise, try to get from address
    const address = practitioner.address?.[0];
    if (address) {
      const city = address.city || '';
      const state = address.state || '';
      const postalCode = address.postalCode || '';
      const addressStr = `${city}, ${state} ${postalCode}`.trim();
      console.log('Using address:', addressStr);
      return addressStr;
    }
    console.log('No location found, using default');
    return 'Location not specified';
  };

  const formatLocationName = (location) => {
    return location.name || location.alias?.[0] || 'Unknown Location';
  };

  const formatLocationAddress = (location) => {
    const address = location.address;
    if (address) {
      const line = address.line?.[0] || '';
      const city = address.city || '';
      const state = address.state || '';
      const postalCode = address.postalCode || '';
      return `${line}, ${city}, ${state} ${postalCode}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, '');
    }
    return 'Address not available';
  };

  const formatLocationType = (location) => {
    return location.type?.coding?.[0]?.display || 
           location.type?.text || 
           'Unknown Type';
  };

  const formatSlotTime = (slot) => {
    const start = new Date(slot.start);
    const end = new Date(slot.end);
    
    const startTime = start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const endTime = end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const date = start.toLocaleDateString();
    
    return `${date} at ${startTime} - ${endTime}`;
  };

  const getAvailableSlotsForDate = (date) => {
    if (!appointmentSlots.length) return [];
    
    const targetDate = new Date(date);
    return appointmentSlots.filter(slot => {
      const slotDate = new Date(slot.start);
      return slotDate.toDateString() === targetDate.toDateString() && 
             (slot.status === 'free' || slot.status === 'proposed' || slot.status === 'pending');
    });
  };

  const getUniqueDates = () => {
    if (!appointmentSlots.length) return [];
    
    const dates = appointmentSlots
      .filter(slot => slot.status === 'free' || slot.status === 'proposed' || slot.status === 'pending')
      .map(slot => new Date(slot.start).toDateString());
    
    return [...new Set(dates)].sort();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Schedule Appointment</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <XMarkIcon className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Error Message */}
          {errorMessage && (
            <div className="mb-4 bg-red-100 border border-red-300 text-red-800 rounded-lg px-4 py-3 flex items-center justify-between">
              <span>{errorMessage}</span>
              <button
                onClick={() => setErrorMessage('')}
                className="ml-4 text-red-600 hover:text-red-800 font-bold text-lg focus:outline-none"
                aria-label="Dismiss error"
              >
                &times;
              </button>
            </div>
          )}
          {/* Step Indicator */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center space-x-4">
              <div className={`flex items-center ${step === 'search' ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                  step === 'search' ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300'
                }`}>
                  1
                </div>
                <span className="ml-2 font-medium">Search</span>
              </div>
              <div className={`w-12 h-0.5 ${step === 'slots' || step === 'confirm' ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
              <div className={`flex items-center ${step === 'slots' || step === 'confirm' ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                  step === 'slots' || step === 'confirm' ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300'
                }`}>
                  2
                </div>
                <span className="ml-2 font-medium">Select Time</span>
              </div>
              <div className={`w-12 h-0.5 ${step === 'confirm' ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
              <div className={`flex items-center ${step === 'confirm' ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                  step === 'confirm' ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300'
                }`}>
                  3
                </div>
                <span className="ml-2 font-medium">Confirm</span>
              </div>
            </div>
          </div>

          {/* Step 1: Practitioner Search */}
          {step === 'search' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Find a Healthcare Provider</h3>
                <p className="text-gray-600 mb-4">Search by name, specialty, or location</p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>Search Tips:</strong> Use name search for specific providers, or specialty search to find all providers in a particular field.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  className={`transition-opacity ${searchParams.specialty ? 'bg-gray-100 opacity-60 cursor-not-allowed' : ''} rounded-lg p-2`}
                >
                  <label className={`block text-sm font-medium mb-2 ${searchParams.specialty ? 'text-gray-400' : 'text-gray-700'}`}>
                    <UserIcon className="h-4 w-4 inline mr-1" />
                    Provider Name
                  </label>
                  <input
                    type="text"
                    placeholder="Dr. Smith"
                    value={searchParams.name}
                    onChange={(e) => {
                      setErrorMessage('');
                      setSearchParams({...searchParams, name: e.target.value});
                    }}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${searchParams.specialty ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
                    disabled={!!searchParams.specialty}
                  />
                </div>
                <div className="rounded-lg p-2 transition-opacity">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MagnifyingGlassIcon className="h-4 w-4 inline mr-1" />
                    Specialty
                  </label>
                  <select
                    value={searchParams.specialty}
                    onChange={(e) => {
                      setErrorMessage('');
                      setSearchParams({
                        ...searchParams,
                        specialty: e.target.value,
                        name: e.target.value ? '' : searchParams.name // clear name if specialty selected
                      });
                    }}
                    disabled={loadingSpecialties}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">
                      {loadingSpecialties ? 'Loading Specialties...' : 'All Specialties'}
                    </option>
                    {!loadingSpecialties && specialties.map(specialty => (
                      <option key={specialty} value={specialty}>{specialty}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="text-center">
                <button
                  onClick={handleSearch}
                  disabled={loadingPractitioners}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loadingPractitioners ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Searching...
                    </div>
                  ) : (
                    'Search Providers'
                  )}
                </button>
              </div>

              {/* Search Results */}
              {practitioners.length > 0 && (
                <div className="mt-8">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Found {practitioners.length} Provider{practitioners.length !== 1 ? 's' : ''}
                  </h4>
                  <div className="space-y-4">
                    {practitioners.map((practitioner, index) => (
                      <div key={practitioner.id || index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h5 className="font-semibold text-gray-900">
                              {formatPractitionerName(practitioner)}
                            </h5>
                            <p className="text-sm text-gray-600">{formatSpecialty(practitioner)}</p>
                            <p className="text-sm text-gray-500">{formatLocation(practitioner)}</p>
                          </div>
                          <button
                            onClick={() => handlePractitionerSelect(practitioner)}
                            className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors"
                          >
                            Book Appointment
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {practitioners.length === 0 && !loadingPractitioners && searchParams.name && (
                <div className="text-center py-8 text-gray-500">
                  <UserIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No providers found matching your criteria</p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Appointment Slots */}
          {step === 'slots' && selectedPractitioner && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Select Appointment Time</h3>
                <p className="text-gray-600 mb-4">
                  Available times for {formatPractitionerName(selectedPractitioner)}
                </p>
                
                {/* Location Information */}
                <div className="max-w-md mx-auto bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <MapPinIcon className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                    <div className="text-left">
                      <h4 className="font-medium text-blue-900 mb-1">Appointment Location</h4>
                      <p className="text-sm text-blue-800">
                        {formatLocation(selectedPractitioner)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Service Type Selection */}
              <div className="max-w-md mx-auto">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Appointment Type
                </label>
                <select
                  value={serviceType}
                  onChange={(e) => setServiceType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {serviceTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {loadingSlots ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Finding available appointment times...</p>
                </div>
              ) : appointmentSlots.length > 0 ? (
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900">
                    Available Appointment Times (Next 30 Days)
                  </h4>
                  
                  {/* Calendar View */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {getUniqueDates().map(dateStr => {
                      const date = new Date(dateStr);
                      const slotsForDate = getAvailableSlotsForDate(date);
                      
                      return (
                        <div key={dateStr} className="border border-gray-200 rounded-lg p-4">
                          <h5 className="font-semibold text-gray-900 mb-3">
                            {date.toLocaleDateString('en-US', { 
                              weekday: 'short', 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </h5>
                          <div className="space-y-2">
                            {slotsForDate.map((slot, index) => (
                              <button
                                key={slot.id || index}
                                onClick={() => handleSlotSelect(slot)}
                                className="w-full text-left p-2 rounded border border-gray-200 hover:bg-blue-50 hover:border-blue-300 transition-colors"
                              >
                                <div className="flex items-center">
                                  <ClockIcon className="h-4 w-4 text-gray-500 mr-2" />
                                  <span className="text-sm font-medium">
                                    {new Date(slot.start).toLocaleTimeString([], { 
                                      hour: '2-digit', 
                                      minute: '2-digit' 
                                    })}
                                  </span>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CalendarIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No available appointment times found</p>
                  <button
                    onClick={() => { setErrorMessage(''); setStep('search'); }}
                    className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Search for different provider
                  </button>
                </div>
              )}

              <div className="text-center">
                <button
                  onClick={() => { setErrorMessage(''); setStep('search'); }}
                  className="text-gray-600 hover:text-gray-800 font-medium"
                >
                  ← Back to Search
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 'confirm' && selectedSlot && selectedPractitioner && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Confirm Appointment</h3>
                <p className="text-gray-600">Please review your appointment details</p>
              </div>

              <div className="max-w-md mx-auto bg-gray-50 rounded-lg p-6">
                <div className="space-y-4">
                  <div className="flex items-center">
                    <UserIcon className="h-5 w-5 text-gray-500 mr-3" />
                    <div>
                      <p className="text-sm text-gray-600">Provider</p>
                      <p className="font-semibold text-gray-900">
                        {formatPractitionerName(selectedPractitioner)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <CalendarIcon className="h-5 w-5 text-gray-500 mr-3" />
                    <div>
                      <p className="text-sm text-gray-600">Date & Time</p>
                      <p className="font-semibold text-gray-900">
                        {formatSlotTime(selectedSlot)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-500 mr-3" />
                    <div>
                      <p className="text-sm text-gray-600">Appointment Type</p>
                      <p className="font-semibold text-gray-900">
                        {serviceTypes.find(type => type.value === serviceType)?.label}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <MapPinIcon className="h-5 w-5 text-gray-500 mr-3" />
                    <div>
                      <p className="text-sm text-gray-600">Location</p>
                      <p className="font-semibold text-gray-900">
                        {formatLocation(selectedPractitioner)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Appointment Note Field */}
              <div className="max-w-md mx-auto">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Appointment Note (Optional)
                </label>
                <textarea
                  value={appointmentNote}
                  onChange={(e) => setAppointmentNote(e.target.value)}
                  placeholder="Add any notes or special requests for this appointment..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows="3"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This note will be included with your appointment booking.
                </p>
              </div>

              <div className="text-center space-y-4">
                <button
                  onClick={handleConfirmBooking}
                  disabled={bookingAppointment}
                  className="bg-green-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {bookingAppointment ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Booking...
                    </div>
                  ) : (
                    <>
                      <CheckIcon className="h-5 w-5 inline mr-2" />
                      Confirm Booking
                    </>
                  )}
                </button>
                
                <div className="space-x-4">
                  <button
                    onClick={() => { setErrorMessage(''); setStep('slots'); }}
                    className="text-gray-600 hover:text-gray-800 font-medium"
                  >
                    ← Back to Times
                  </button>
                  <button
                    onClick={() => { setErrorMessage(''); setStep('search'); }}
                    className="text-gray-600 hover:text-gray-800 font-medium"
                  >
                    ← Back to Search
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentBookingModal; 